import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Lightbulb } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { intelligenceApi } from "@/api/intelligence";
import { superAdminApi } from "@/api/superAdmin";
import { defaultFinancialReportHospitalId } from "@/constants/pnlDefaultScope";
import { intelligenceReportHospitalId } from "@/hooks/useChainReportHospitalScope";
import { IntelligenceSubNav } from "@/components/intelligence/IntelligenceSubNav";
import { IntelligenceServerExportButton } from "@/components/intelligence/IntelligenceServerExportButton";
import { IntelligenceListPagination } from "@/components/intelligence/IntelligenceListPagination";
import { IntelligenceSeverityBadge } from "@/components/intelligence/IntelligenceSeverityBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

type Props = { basePath?: string };

export default function InsightsIntelligencePage({ basePath }: Props) {
  const { user, activeHospitalId, isChainAdmin } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const isInvestor = user?.role === "INVESTOR";
  const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
  const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
  const chainHospitals = user?.hospitals ?? [];
  const isChainScoped = isChainAdmin || investorHasChainScope;
  const [searchParams] = useSearchParams();
  const selectedInsightId = searchParams.get("insight");
  const qc = useQueryClient();

  const defaultFinancialHospitalId = useMemo(
    () =>
      defaultFinancialReportHospitalId({
        isSuperAdmin,
        isChainScoped,
        activeHospitalId,
        fallbackHospitalId: chainHospitals[0]?.id ?? null,
      }),
    [isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
  );

  const [selectedHospitalId, setSelectedHospitalId] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [resolutionNote, setResolutionNote] = useState("");
  const pageSize = 25;

  useEffect(() => {
    if (!selectedHospitalId && defaultFinancialHospitalId !== undefined) {
      setSelectedHospitalId(defaultFinancialHospitalId || "all");
    }
  }, [defaultFinancialHospitalId, selectedHospitalId]);

  const { data: superHospitals } = useQuery({
    queryKey: ["super-admin-hospitals-insights"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin,
  });

  const reportHospitalId = useMemo(
    () =>
      intelligenceReportHospitalId({
        showHospitalFilter,
        selectedHospitalId,
        isChainScoped,
      }),
    [showHospitalFilter, selectedHospitalId, isChainScoped]
  );

  useEffect(() => {
    setPage(1);
  }, [reportHospitalId, statusFilter, typeFilter]);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["intelligence-insights", reportHospitalId, statusFilter, typeFilter, page],
    queryFn: () =>
      intelligenceApi.getInsights({
        hospitalId: reportHospitalId,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        page,
        pageSize,
      }),
  });

  const detailQuery = useQuery({
    queryKey: ["intelligence-insight-detail", selectedInsightId],
    queryFn: () => intelligenceApi.getInsightDetail(selectedInsightId!),
    enabled: !!selectedInsightId,
  });

  const acknowledgeMut = useMutation({
    mutationFn: (id: string) => intelligenceApi.acknowledgeInsight(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["intelligence-insights"] });
      void qc.invalidateQueries({ queryKey: ["intelligence-insight-detail"] });
    },
  });

  const resolveMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => intelligenceApi.resolveInsight(id, note),
    onSuccess: () => {
      setResolutionNote("");
      void qc.invalidateQueries({ queryKey: ["intelligence-insights"] });
      void qc.invalidateQueries({ queryKey: ["intelligence-insight-detail"] });
    },
  });

  const dismissMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => intelligenceApi.dismissInsight(id, note),
    onSuccess: () => {
      setResolutionNote("");
      void qc.invalidateQueries({ queryKey: ["intelligence-insights"] });
      void qc.invalidateQueries({ queryKey: ["intelligence-insight-detail"] });
    },
  });

  const exportParams = {
    hospitalId: reportHospitalId,
    status: statusFilter || undefined,
    type: typeFilter || undefined,
  };

  const detail = detailQuery.data;
  const navBase = basePath ?? (isSuperAdmin ? "/super-admin" : "/hospital-admin");

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4">
      <IntelligenceSubNav basePath={navBase} />
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Lightbulb className="h-6 w-6 text-indigo-600" />
          Business Insights
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Deterministic operational observations from KPI and domain intelligence.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {showHospitalFilter && (
          <select className="border rounded-md px-3 py-1.5 text-sm" value={selectedHospitalId} onChange={(e) => setSelectedHospitalId(e.target.value)}>
            <option value="all">All hospitals</option>
            {(isSuperAdmin ? superHospitals : chainHospitals)?.map((h) => (
              <option key={h.id} value={h.id}>{h.name}</option>
            ))}
          </select>
        )}
        <select className="border rounded-md px-3 py-1.5 text-sm" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="ACKNOWLEDGED">Acknowledged</option>
          <option value="RESOLVED">Resolved</option>
          <option value="DISMISSED">Dismissed</option>
        </select>
        <select className="border rounded-md px-3 py-1.5 text-sm" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          <option value="GROWTH">Growth</option>
          <option value="DECLINE">Decline</option>
          <option value="OPPORTUNITY">Opportunity</option>
          <option value="RISK">Risk</option>
          <option value="EFFICIENCY">Efficiency</option>
        </select>
        <IntelligenceServerExportButton
          report="insights"
          params={exportParams}
          filename="business-insights-export"
          disabled={isLoading}
        />
      </div>

      {isLoading && (
        <div className="flex items-center gap-2 py-12 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading insights…
        </div>
      )}

      {isError && (
        <Card className="border-destructive/50">
          <CardContent className="pt-4 text-sm text-destructive">{(error as Error)?.message ?? "Failed to load insights"}</CardContent>
        </Card>
      )}

      {data && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle className="text-base">Insights ({data.pagination.total})</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[600px] overflow-auto">
              {data.insights.length === 0 ? (
                <p className="text-sm text-muted-foreground">No insights match filters.</p>
              ) : (
                data.insights.map((ins) => (
                  <Link key={ins.id} to={`?insight=${ins.id}`} className={`block border rounded-md p-3 text-sm hover:bg-muted/40 ${selectedInsightId === ins.id ? "ring-2 ring-indigo-300" : ""}`}>
                    <div className="flex flex-wrap gap-2 items-center">
                      <IntelligenceSeverityBadge severity={ins.severity} />
                      {ins.insightType && <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{ins.insightType}</span>}
                      <span className="font-medium">{ins.title}</span>
                    </div>
                    <p className="text-muted-foreground mt-1 line-clamp-2">{ins.description}</p>
                  </Link>
                ))
              )}
              <IntelligenceListPagination
                page={data.pagination.page}
                pageSize={data.pagination.pageSize}
                total={data.pagination.total}
                onPageChange={setPage}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Insight detail</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {!detail ? (
                <p className="text-muted-foreground">Select an insight to view details.</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2"><IntelligenceSeverityBadge severity={detail.severity} /><span className="font-semibold">{detail.title}</span></div>
                  <p>{detail.description}</p>
                  {detail.recommendedAction && <p><span className="text-muted-foreground">Recommendation:</span> {detail.recommendedAction}</p>}
                  {detail.changePercent != null && (
                    <p><span className="text-muted-foreground">Variance:</span> {detail.changePercent}%</p>
                  )}
                  <p><span className="text-muted-foreground">Status:</span> {detail.status}</p>
                  {detail.drillDownPath && (
                    <Link to={detail.drillDownPath.replace("/hospital-admin", navBase)} className="text-indigo-600 hover:underline">View related report →</Link>
                  )}
                  {detail.status === "OPEN" && (
                    <Button size="sm" variant="outline" onClick={() => acknowledgeMut.mutate(detail.id)} disabled={acknowledgeMut.isPending}>Acknowledge</Button>
                  )}
                  {(detail.status === "OPEN" || detail.status === "ACKNOWLEDGED") && (
                    <div className="space-y-2 pt-2 border-t">
                      <textarea className="w-full border rounded-md p-2 text-sm" rows={3} placeholder="Resolution note (required)" value={resolutionNote} onChange={(e) => setResolutionNote(e.target.value)} />
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => resolveMut.mutate({ id: detail.id, note: resolutionNote })} disabled={!resolutionNote.trim() || resolveMut.isPending}>Resolve</Button>
                        <Button size="sm" variant="secondary" onClick={() => dismissMut.mutate({ id: detail.id, note: resolutionNote })} disabled={!resolutionNote.trim() || dismissMut.isPending}>Dismiss</Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
